import { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Link,
  Paper,
  Skeleton,
  TableBody,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
} from "@mui/material";
import { useNavigate } from "react-router-dom";

import { formatDateTimeWithSeconds } from "../../utils/DateTimeUtils";
import { SosAlertService } from "../../services/SosAlertService";
import {
  CustomTableRowTR,
  HorizontalContainer,
  Paragraph,
  ShowValue,
  StyledTable,
  TableData,
  TableHeader,
  TableWrapper,
} from "../../styles/commonStyles";
import CustomCallIconButton from "./CallButton/CustomCallIconButton";
import SosUpdateDialog from "./SosUpdateDialog/SosUpdateDialog";
import { UserType } from "../../enums/common";
import { maskPhoneNumber } from "../../utils/PhoneNumberUtils";

const sosAlertService = new SosAlertService();
const SKELETON_ROWS = 5;

const getStatusStyles = (status?: string) => {
  switch (status) {
    case "OPEN":
      return { color: "#1D4ED8", background: "rgba(37, 99, 235, 0.12)" };
    case "IN_PROGRESS":
      return { color: "#B45309", background: "rgba(245, 158, 11, 0.18)" };
    case "RESOLVED":
      return { color: "#047857", background: "rgba(16, 185, 129, 0.16)" };
    case "CLOSED":
      return { color: "#6B7280", background: "rgba(107, 114, 128, 0.16)" };
    default:
      return { color: "#6B7280", background: "rgba(107, 114, 128, 0.08)" };
  }
};

type OmsSosAlertProps = {
  referenceId: number;
  referenceModel: "ONE_MEAL_SERVICE" | "CHEF_FOR_PARTY";
};

const ReferenceSosAlertsTable = ({ referenceId, referenceModel }: OmsSosAlertProps) => {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<any[]>([]);
  const [error, setError] = useState<string | null>(null);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [selectedRow, setSelectedRow] = useState<any | null>(null);

  const isMountedRef = useRef(true);

  const loadData = useCallback(async () => {
    if (!referenceId || !referenceModel) return;

    setLoading(true);
    setError(null);

    try {
      const response = await sosAlertService.getAllSosAlerts(
        rowsPerPage,
        page * rowsPerPage,
        {
          referenceId,
          referenceModel,
        },
        { field: "id", order: "DESC" }
      );

      if (!isMountedRef.current) return;

      setData(Array.isArray(response.data) ? response.data : []);
      setTotal(Number(response.total) || 0);
    } catch (err) {
      console.error("Failed to fetch SOS alerts", err);
      if (!isMountedRef.current) return;

      setError("Failed to load SOS alerts. Please try again.");
      setData([]);
      setTotal(0);
    } finally {
      if (isMountedRef.current) setLoading(false);
    }
  }, [referenceId, referenceModel, page, rowsPerPage]);

  useEffect(() => {
    loadData();
    return () => {
      isMountedRef.current = false;
    };
  }, [loadData]);


  if (error) {
    return (
      <Box py={2}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <>
    <br />
      <TableWrapper>

        <TableContainer component={Paper} sx={{ borderRadius: "8px" }}>
          <StyledTable>
            <TableHead>
              <TableRow sx={{ backgroundColor: "rgba(232,235,237,0.3) !important" }}>
                <TableHeader>Actions</TableHeader>
                <TableHeader>Id</TableHeader>
                <TableHeader>Status</TableHeader>
                <TableHeader>Reference Id</TableHeader>
                <TableHeader>Reference Model</TableHeader>
                <TableHeader>Cook</TableHeader>
                <TableHeader>Alert Reason</TableHeader>
                <TableHeader>Query Description</TableHeader>
                <TableHeader>Comments</TableHeader>
              </TableRow>
            </TableHead>

            {loading &&
              Array.from({ length: SKELETON_ROWS }).map((_, i) => (
                <CustomTableRowTR key={i}>
                  {Array.from({ length: 9 }).map((__, j) => (
                    <TableData key={j}>
                      <Skeleton height={28} />
                    </TableData>
                  ))}
                </CustomTableRowTR>
              ))}

            <TableBody>
              {!loading && data.length > 0 ? (
                data.map((row) => (
                  <CustomTableRowTR
                    key={row.id}
                    onClick={() =>
                      navigate(`/SosAlerts/${row.refernceModelName}/${row.id}`)
                    }
                  >
                    <TableData onClick={(e) => e.stopPropagation()}>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => setSelectedRow(row)}
                      >
                        Update
                      </Button>
                    </TableData>
                    <TableData>{row.id}</TableData>

                    <TableData>
                      <Paragraph
                        style={{
                          ...getStatusStyles(row?.status),
                          display: "inline-block",
                          padding: "4px 20px",
                          borderRadius: "4px",
                          fontSize: "14px",
                        }}
                      >
                        {row?.status || "-"}
                      </Paragraph>
                    </TableData>

                    <TableData>
                      <Link
                        href={`/#/${
                          row.refernceModelName === "ONE_MEAL_SERVICE"
                            ? "OneMealService"
                            : "ChefForParty"
                        }/${row.referenceId}/show`}
                        underline="hover"
                      >
                        {row.referenceId}
                      </Link>
                    </TableData>

                    <TableData>
                      {row.refernceModelName === "ONE_MEAL_SERVICE"
                        ? "Chefit"
                        : "Party"}
                    </TableData>

                    <TableData onClick={(e) => e.stopPropagation()}>
                      <div>
                        <ShowValue style={{ marginBottom: 4 }}>
                          {row.cookId ? (
                            <Link href={`/#/Cooks/${row.cookId}/show`} underline="hover">
                              {[row.cookProfile?.firstName, row.cookProfile?.lastName]
                                .filter(Boolean)
                                .join(" ") || `Cook #${row.cookId}`}
                            </Link>
                          ) : (
                            "-"
                          )}
                        </ShowValue>
                        <HorizontalContainer>
                          {row.cookProfile?.mobile ? (
                            <CustomCallIconButton
                              color="#6C7C93"
                              size={18}
                              toPhoneNumber={row.cookProfile.mobile}
                              userType={UserType.COOK}
                              referenceId={Number(row.referenceId)}
                              referenceModelName={row.refernceModelName}
                            />
                          ) : null}
                          <ShowValue style={{ marginBottom: 0 }}>
                            {maskPhoneNumber(row.cookProfile?.mobile || "") || "-"}
                          </ShowValue>
                        </HorizontalContainer>
                      </div>
                    </TableData>

                    <TableData>{row.queryDetails?.trEn || "-"}</TableData>
                    <TableData>{row.queryDesc || "-"}</TableData>

                    <TableData onClick={(e) => e.stopPropagation()}>
                      {row.comments?.length ? (
                        <TableContainer component={Paper} sx={{ borderRadius: "8px", minWidth: 360 }}>
                          <StyledTable size="small">
                            <TableHead>
                              <TableRow sx={{ backgroundColor: "rgba(232, 235, 237, 0.3) !important" }}>
                                <TableHeader>Comment</TableHeader>
                                <TableHeader>Commenter email</TableHeader>
                                <TableHeader>Timestamp</TableHeader>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {row.comments.map((each: any) => (
                                <CustomTableRowTR key={each.id}>
                                  <TableData>{each?.comment}</TableData>
                                  <TableData>{each?.commenterEmail}</TableData>
                                  <TableData>
                                    {each?.timestamp ? formatDateTimeWithSeconds(each?.timestamp) : "-"}
                                  </TableData>
                                </CustomTableRowTR>
                              ))}
                            </TableBody>
                          </StyledTable>
                        </TableContainer>
                      ) : (
                        <span>Not Available</span>
                      )}
                    </TableData>

                  </CustomTableRowTR>
                ))
              ) : (
                !loading && (
                  <TableRow>
                    <TableHeader colSpan={9} align="center">
                      No data available
                    </TableHeader>
                  </TableRow>
                )
              )}
            </TableBody>
          </StyledTable>
        </TableContainer>
      </TableWrapper>

      {/* Pagination */}
      {!loading && total > 0 && (
        <TablePagination
          component="div"
          count={total}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={(_, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(Number(e.target.value));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50]}
        />
      )}

      {selectedRow && (
        <SosUpdateDialog
          open={!!selectedRow}
          onClose={() => setSelectedRow(null)}
          sosId={Number(selectedRow.id)}
          currentStatus={selectedRow.status}
          referenceModelName={selectedRow.refernceModelName ?? referenceModel}
          alertReason={selectedRow?.queryDetails?.trEn}
          onSuccess={loadData}
        />
      )}
    </>
  );
};

export default ReferenceSosAlertsTable;
